import { useCallback, useEffect } from 'react';
import { dispatch, useSelector } from '../redux/store';
import { getLeadPins } from '../redux/slices/leadPins';
import useGetCurrentUserId from './useGetCurrentUserId';

// Shared access to the practice's pinned leads. Fetches once per practice
// (unless `autoLoad` is false) and exposes the current PracticeUser id so
// callers can tell their own pins apart from colleagues'.
export default function useLeadPins({ autoLoad = true } = {}) {
    const practiceId = useSelector((state) => state.practiceTreatmentList?.practiceId);
    const { pins, loaded, loading, practiceId: loadedPracticeId } = useSelector((state) => state.leadPins);
    const practiceUserId = useGetCurrentUserId();

    const loadedForPractice = Boolean(loaded && practiceId != null && loadedPracticeId != null)
        && String(loadedPracticeId) === String(practiceId);

    // Refetch whenever the slice doesn't hold THIS practice's list — including
    // practice changes that bypass practiceSwitchReset, where the slice still
    // carries the previous practice's pins (never rendered: see `pins` below).
    // No `loading` short-circuit: a dropped stale request must not block the
    // fetch for the new practice (identical GETs are deduped by callApi).
    useEffect(() => {
        if (!autoLoad || !practiceId || loadedForPractice) return;
        dispatch(getLeadPins(practiceId));
    }, [autoLoad, practiceId, loadedForPractice]);

    const refresh = useCallback(() => {
        if (practiceId) dispatch(getLeadPins(practiceId));
    }, [practiceId]);

    return {
        practiceId,
        pins: loadedForPractice ? pins : [],
        loaded: loadedForPractice,
        loading,
        practiceUserId,
        refresh,
    };
}
